import ProductCard from '@components/ProductCard';
import TabNavigation from '@components/TabNavigation';
import Link from '@utility/Link';
import ScrollInView from '@utility/ScrollInView';
import cn from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import SwiperCore, { Navigation, Scrollbar } from 'swiper/core';
import { Swiper, SwiperSlide } from 'swiper/react';
import * as styles from './styles';
import { viewItemListDL } from '@src/helpers/dataLayerHelper';
import { useLocation } from '@reach/router';

SwiperCore.use([Navigation, Scrollbar]);

const ProductCarousel = ({
  data: { title, subtitle, description, link, featuredProducts },
  productByCollection,
}) => {
  const location = useLocation();
  const [visibleProduct, setVisibleProduct] = useState('');
  const [updateSwiper, setUpdateSwiper] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  
  // Sort the data array based on the orderRank (Sanity) in ascending order
  const sortedProducts = featuredProducts?.length > 0 && featuredProducts[0].orderRank ? featuredProducts.sort((a, b) => a.orderRank?.localeCompare(b.orderRank)) : featuredProducts;

  const params = {
    slidesPerView: 'auto',
    speed: 600,
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
      enabled: true,
    },
    scrollbar: {
      el: '.swiper-scrollbar',
      hide: false,
      enabled: true,
    },
    observer: true,
    observeParents: true,
    observeSlideChildren: true,
  };

  const onClickHandler = (id) => {
    setVisibleProduct(id);
    setUpdateSwiper(updateSwiper + 1);
  };

  useEffect(() => {
    if (!!swiperInstance && productByCollection && productByCollection.length) {
      swiperInstance.update();
      swiperInstance.navigation.update();
      swiperInstance.scrollbar.updateSize();
      swiperInstance.slideTo(0);
    }
  }, [updateSwiper, swiperInstance]);

  useEffect(() => {
    setHydrated(true);
  }, [])

  useEffect(() => {
    if (!hydrated) return; // Ensure it only runs after hydration
    const listName = location.pathname.includes('/products/') ? 'Related Products' : 'Feature Carousel';
    const items = featuredProducts?.length > 0 ? featuredProducts.map(e => e.title) : productByCollection.map(e => e.title) 
    viewItemListDL(listName, items);
  }, [hydrated]);

  const renderProductTypes = () => {
    const types = [];
    // push all the different product types into a new array
    productByCollection.map((el) =>
      el.node.productType.map((type) => types.push(type)),
    );
    // filter out duplicates based on the productType.id
    const filteredTypes = types.filter(
      (obj, pos, arr) => arr.map((mapObj) => mapObj.id).indexOf(obj.id) === pos,
    );
    if (filteredTypes.length) {
      const buttons = filteredTypes.map((el) => (
        <li key={el.id}>
          <button
            type="button"
            data-type-id={el.id}
            onClick={() => onClickHandler(el.id)}
            className={cn({ 'is-active': visibleProduct === el.id })}
          >
            {el.title}
          </button>
        </li>
      ));
      return (
        <div className="flex">
          <div className={cn(styles.intro, 'hidden md:block')} />
          {buttons.length > 1 && (
            <TabNavigation className="px-6 w-2/3">
              <li>
                <button
                  type="button"
                  onClick={() => onClickHandler('')}
                  className={cn({ 'is-active': visibleProduct === '' })}
                >
                  All
                </button>
              </li>
              {buttons}
            </TabNavigation>
          )}
        </div>
      );
    }
  };

  const renderIntro = (className) => (
    <SwiperSlide className={cn(styles.intro, className)}>
      {/* {subtitle && (
        <p className="type-upper-070 text-mono-400 mt-1 mb-2">{subtitle}</p>
      )} */}
      <h2 className="type-sans-630 mb-6 lg:type-sans-930 md:pr-12">{title}</h2>
      {description && (
        <p
          className="type-sans-330 text-mono-500 mb-10 md:mr-16"
          id={`${title}-description`}
        >
          {description}
        </p>
      )}
      {link && link[0] && (
        <p className="mb-10 -mt-2">
          <Link
            internalExternal
            to={link[0]}
            className="type-sans-030"
            aria-label={link[0].label}
            aria-describedby={`${title}-description`}
          >
            {link[0].label}
          </Link>
        </p>
      )}
    </SwiperSlide>
  );

  const isVisible = (el) =>
    visibleProduct === '' ||
    el.node.productType.flatMap(({ id }) => id).includes(visibleProduct);

  const updatedTimeProductByCollection = productByCollection.length
    ? productByCollection.sort(function (a, b) {
        let aDate = new Date(a.node._updatedAt);
        let bDate = new Date(b.node._updatedAt);

        return aDate === bDate
          ? a.node.title.localeCompare(b.node.title, undefined, {
              numeric: true,
              sensitivity: 'base',
            })
          : bDate.getTime() - aDate.getTime();
      })
    : null;

  return (
    <div className="print:hidden">
      <ScrollInView>
        {featuredProducts && (
          <>
            <div className="wrapper md:hidden">
              <div className="row">
                <div className="col-12">{renderIntro()}</div>
              </div>
            </div>
            <div>
              <Swiper {...params}>
                {renderIntro('hidden md:block')}
                {sortedProducts.map((el, i) => (
                  <SwiperSlide key={`${el._id}-${i}`} className={styles.card}>
                    <ProductCard data={{ ...el }} showPrice={false} />
                  </SwiperSlide>
                ))}
                <button
                  className="swiper-button-next"
                  aria-label="Next Button"
                ></button>
                <button
                  className="swiper-button-prev"
                  aria-label="Previous Button"
                ></button>
                <div className="swiper-scrollbar"></div>
              </Swiper>
            </div>
          </>
        )}
        {!!productByCollection.length && (
          <>
            <div className="wrapper md:hidden">
              <div className="row">
                <div className="col-12">{renderIntro()}</div>
              </div>
            </div>
            {renderProductTypes()}
            <div>
              <Swiper
                {...params}
                onSwiper={(instance) => setSwiperInstance(instance)}
              >
                {renderIntro('hidden md:block')}
                {updatedTimeProductByCollection
                  .filter((el) => isVisible(el))
                  .map((el) => (
                    <SwiperSlide key={el.node.id} className={styles.card}>
                      <ProductCard data={{ ...el.node }} finishCode="" />
                    </SwiperSlide>
                  ))}
                <button
                  className="swiper-button-next"
                  aria-label="Next Button"
                ></button>
                <button
                  className="swiper-button-prev"
                  aria-label="Previous Button"
                ></button>
                <div className="swiper-scrollbar"></div>
              </Swiper>
            </div>
          </>
        )}
      </ScrollInView>
    </div>
  );
};

ProductCarousel.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    description: PropTypes.string,
    link: PropTypes.arrayOf(PropTypes.object),
    featuredProducts: PropTypes.arrayOf(PropTypes.object),
  }),
  productByCollection: PropTypes.arrayOf(PropTypes.object),
};

ProductCarousel.defaultProps = {
  data: {
    subtitle: '',
    description: '',
    link: [],
    featuredProducts: [],
  },
  productByCollection: [],
};

export default ProductCarousel;
